/**
 * Fullscreen "Emulation mode" watermark overlay.
 *
 * Vue parity — renders the equivalent of `<n-watermark fullscreen
 * content="Emulation mode" rotate="-15">` from
 * `RW-ERP-Service/src/pages/app/products/draftproducts/components/UserEmulation.vue:13-24`.
 *
 * Visual: tiled diagonal "Emulation mode" text covering the entire
 * viewport. Click-through (`pointer-events: none`), painted behind
 * nothing — sits above page content but does not block interaction.
 *
 * Visibility: renders only when `localStorage.isEmulate === 'true'`.
 * Reactive to cross-tab toggles via the `storage` event so the
 * watermark appears/disappears the moment Vue (or this app) flips
 * emulation on/off, no reload required.
 */
import React, { useEffect, useState } from 'react';

import { STORAGE_KEYS } from '../../../constants/storageKeys';
import { readEmulationState } from '../../../utils/emulation';

/*
  SVG background, URL-encoded inline to avoid an extra request.
  The SVG paints "Emulation mode" rotated -15° inside a 384×384 tile
  (matching Vue's `:width` / `:height` / `:rotate`).
  Naive-UI's `cross` prop offsets every other row — replicated by
  duplicating the text node at `y=192` (half the tile height).
  Dark-theme variant uses white strokes; light-theme variant uses black
  strokes so the watermark remains visible against light page backgrounds.
  We pick the variant at render time by reading `document.documentElement`'s
  `data-theme` attribute. We can't keep the previous CSS-only theme switch
  because Tailwind's static scanner cannot see template-interpolated
  classes for arbitrary background-image URLs.
*/
const WATERMARK_DARK_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='384' height='384' viewBox='0 0 384 384'%3E%3Cg transform='rotate(-15 192 192)' fill='%23ffffff' fill-opacity='0.08' font-family='Arial,Helvetica,sans-serif' font-size='16' font-weight='600'%3E%3Ctext x='12' y='80'%3EEmulation mode%3C/text%3E%3Ctext x='204' y='272'%3EEmulation mode%3C/text%3E%3C/g%3E%3C/svg%3E\")";
const WATERMARK_LIGHT_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='384' height='384' viewBox='0 0 384 384'%3E%3Cg transform='rotate(-15 192 192)' fill='%23000000' fill-opacity='0.07' font-family='Arial,Helvetica,sans-serif' font-size='16' font-weight='600'%3E%3Ctext x='12' y='80'%3EEmulation mode%3C/text%3E%3Ctext x='204' y='272'%3EEmulation mode%3C/text%3E%3C/g%3E%3C/svg%3E\")";

// CRITICAL — `pointer-events-none` must never intercept clicks. The
// whole page sits behind it.
const WATERMARK_CLASS =
  'fixed inset-0 z-[9999] pointer-events-none bg-repeat bg-left-top';

const readThemeIsLight = (): boolean =>
  typeof document !== 'undefined'
  && document.documentElement.getAttribute('data-theme') === 'light';

export const EmulationWatermark: React.FC = () => {
  const [emulating, setEmulating] = useState<boolean>(
    () => readEmulationState().active,
  );
  const [isLight, setIsLight] = useState<boolean>(() => readThemeIsLight());

  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      // Only react when the emulation flag actually changes. Other
      // storage writes (auth token rotation, locale, etc.) shouldn't
      // re-render the watermark.
      if (
        event.key !== STORAGE_KEYS.isEmulate
        && event.key !== null /* localStorage.clear() */
      ) {
        return;
      }
      setEmulating(readEmulationState().active);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Observe `data-theme` flips on <html> so the SVG stroke colour
  // updates the moment ThemeContext toggles light/dark.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    const observer = new MutationObserver(() => setIsLight(readThemeIsLight()));
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  if (!emulating) return null;
  return (
    <div
      className={WATERMARK_CLASS}
      style={{ backgroundImage: isLight ? WATERMARK_LIGHT_BG : WATERMARK_DARK_BG }}
      aria-hidden="true"
    />
  );
};

export default EmulationWatermark;
